const BASE_MESSAGES = {
  eyebrow: "Reservation settings",
  title: "Set the reservation basics before going live",
  copy:
    "Define the operational basics guests and your team will rely on from day one. The locked blocks below are starter defaults and can be refined later in Settings.",
  loadError: "We could not load the reservation settings draft.",
  saveError: "We could not save the reservation settings right now.",
  saveSuccess: "Your reservation settings draft has been saved.",
  validationTimes: "Check-in and check-out times are required.",
  validationAgeRange: "The child age limit cannot be lower than the baby age limit.",
  stayTitle: "Stay settings",
  stayCopy: "Choose the standard check-in and check-out times guests should see from the start.",
  checkinTime: "Check-in time",
  checkoutTime: "Check-out time",
  agesTitle: "Guest ages",
  agesCopy: "Define the default age ranges Pilcore will use during the first reservation setup.",
  maxAgeBaby: "Max age for baby",
  maxAgeChild: "Max age for child",
  minAgeMainGuest: "Minimum age for main guest",
  minAgeBooker: "Minimum age for booker",
  requiredFieldsTitle: "Required customer fields by role",
  requiredFieldsCopy:
    "Choose which guest details must be captured for each role during reservation creation.",
  requiredFieldsHint:
    "This can be limiting for direct reservations. A direct reservation will never be created unless these fields are completed.\n\nFor booking-engine, OTA or agency reservations, the rule used will be the one defined by each OTA, agency or the booking engine itself, so reservations may still arrive without meeting the required fields configured here.",
  fieldsSelected: (count) => `${count} selected`,
  requiredRolesTitle: "Required customer roles",
  requiredRolesCopy:
    "This startup configuration is locked during onboarding. We recommend beginning with Booker only and adjusting it later if needed.",
  requiredRolesHint: "Recommended starter setup. You can change it later in Settings after onboarding.",
  reservationFlowTitle: "Reservation flow settings",
  reservationFlowCopy:
    "For onboarding we leave the hotel starting in Confirmed mode. This is the recommended default and can be changed later if your workflow needs something different.",
  reservationFlowHint: "Locked during onboarding. Recommended starter mode.",
  requestedTitle: "Requested",
  requestedCopy: "The team reviews the reservation before moving it to confirmed.",
  confirmedTitle: "Confirmed",
  confirmedCopy: "The reservation lands directly as confirmed when it is saved.",
  recommendedTag: "Recommended",
  lockedTag: "Locked",
  confirmationTitle: "Confirmation Email Studio",
  confirmationCopy:
    "This shows the standard confirmation email that will be configured at startup. It is informative only here and can be redesigned later after onboarding.",
  confirmationHint:
    "The starter email content is shown here for reference only. During onboarding you can choose the send language or languages, and the template content can be edited later in Settings.",
  confirmationLanguage: "Language",
  confirmationLanguagesLabel: "Send languages",
  confirmationSubject: "Subject",
  confirmationBody: "Body",
  validationConfirmationLanguages: "Select at least one language for confirmation emails.",
  externalNotificationTitle: "Hotel Notification Studio",
  externalNotificationCopy:
    "Choose whether the hotel should receive an internal email whenever a reservation coming from outside Pilcore, such as an OTA, an agency or a channel manager, is created, modified or cancelled.",
  externalNotificationHint:
    "Onboarding only captures the activation and the recipient list. Direct reservations created inside Pilcore are not part of this alert, and the email content can be refined later in Settings.",
  externalNotificationToggleLabel: "Enable hotel emails for external reservation changes",
  externalNotificationToggleCopy:
    "When enabled, Pilcore sends an internal alert for each external reservation event.",
  externalNotificationRecipientsLabel: "Recipient emails",
  externalNotificationRecipientsHint:
    "Press Enter or Add to create chips. You can also paste multiple recipients separated by commas, semicolons or line breaks.",
  externalNotificationRecipientPlaceholder: "reservations@hotel.com",
  externalNotificationRecipientEmpty: "No recipients configured yet.",
  externalNotificationAddAction: "Add",
  externalNotificationRemoveAction: "Remove",
  externalNotificationEnabledTag: "Enabled",
  externalNotificationDisabledTag: "Disabled",
  validationExternalNotificationRecipients:
    "Add at least one recipient email if hotel notifications are enabled.",
  summaryTitle: "Quick overview",
  summaryIntro:
    "This step combines editable reservation basics and hotel alerts with three locked starter defaults.",
  summaryPoints: [
    "Save once the editable sections reflect how the hotel should start operating.",
    "Required roles, reservation flow and confirmation email stay locked during onboarding.",
    "Anything shown as recommended here can still be adjusted later in the live Settings area.",
  ],
  summaryCheckinLabel: "Check-in",
  summaryCheckoutLabel: "Check-out",
  summaryAgeLabel: "Guest ages",
  summaryFieldsLabel: "Required fields selected",
  summaryRolesLabel: "Required roles",
  summaryFlowLabel: "Flow mode",
  summaryEmailLabel: "Send languages",
  summaryNotificationLabel: "Hotel alerts",
  summaryNotSaved: "Not saved yet",
  summarySavedLabel: "Last save",
  summaryEmailValue: "Standard starter email",
  summaryNotificationDisabled: "Disabled",
  summaryNotificationEnabled: (count) =>
    count === 1 ? "Enabled - 1 recipient" : `Enabled - ${count} recipients`,
  roles: {
    booker: {
      label: "Booker",
      description: "Reservation owner, billing anchor and outbound communication reference.",
    },
    main_guest: {
      label: "Main guest",
      description: "Primary staying guest attached to the room and operations.",
    },
    accompanying: {
      label: "Accompanying guest",
      description: "Additional adult guest staying under the reservation.",
    },
    child: {
      label: "Children",
      description: "Child profiles captured under the reservation.",
    },
  },
  customerFields: [
    { value: "salutation", label: "Salutation" },
    { value: "first_name", label: "First name" },
    { value: "middle_name", label: "Middle name" },
    { value: "last_name", label: "Last name" },
    { value: "second_last_name", label: "Second last name" },
    { value: "gender", label: "Gender" },
    { value: "birth_date", label: "Birth date" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "country", label: "Country" },
    { value: "city", label: "City" },
    { value: "address", label: "Address" },
    { value: "postal_code", label: "Postal code" },
    { value: "document_type", label: "Document type" },
    { value: "document_number", label: "Document number" },
    { value: "is_vip", label: "Is VIP" },
    { value: "vip_level", label: "VIP level" },
    { value: "photo_url", label: "Photo URL" },
    { value: "customer_description", label: "Customer description" },
  ],
  confirmationLanguages: {
    en: "English",
    es: "Spanish",
  },
};

const ES_MESSAGES = {
  ...BASE_MESSAGES,
  title: "Define las bases de la reserva antes de salir a produccion",
  copy:
    "Configura aqui lo esencial para que el hotel empiece con una base operativa clara. Los bloques bloqueados son ajustes iniciales recomendados y podran cambiarse mas adelante en Settings.",
  loadError: "No hemos podido cargar el borrador de reservation settings.",
  saveError: "No hemos podido guardar reservation settings ahora mismo.",
  saveSuccess: "El borrador de reservation settings se ha guardado correctamente.",
  validationTimes: "La hora de check-in y la de check-out son obligatorias.",
  validationAgeRange: "La edad maxima de nino no puede ser inferior a la de bebe.",
  stayTitle: "Stay settings",
  stayCopy: "Elige la hora estandar de check-in y check-out que se mostrara desde el inicio.",
  checkinTime: "Hora de check-in",
  checkoutTime: "Hora de check-out",
  agesTitle: "Edades de huespedes",
  agesCopy: "Define los rangos de edad iniciales que Pilcore usara en la configuracion de reservas.",
  maxAgeBaby: "Edad maxima de bebe",
  maxAgeChild: "Edad maxima de nino",
  minAgeMainGuest: "Edad minima del main guest",
  minAgeBooker: "Edad minima del booker",
  requiredFieldsTitle: "Datos obligatorios por rol",
  requiredFieldsCopy: "Elige que datos deben pedirse para cada rol al crear una reserva.",
  requiredFieldsHint:
    "Esto puede ser limitante para las reservas directas. No se hara ninguna reserva directa si esos datos no estan informados.\n\nEn reservas de motor de reservas, OTAs o agencias, el criterio sera el marcado por cada OTA, agencia o el propio motor de reservas, por lo que podran entrar reservas que no cumplan los requerimientos de datos obligatorios configurados aqui.",
  fieldsSelected: (count) => `${count} seleccionados`,
  requiredRolesTitle: "Required customer roles",
  requiredRolesCopy:
    "Esta configuracion inicial queda bloqueada durante el onboarding. Recomendamos empezar solo con Booker y ajustarlo despues si hace falta.",
  requiredRolesHint: "Configuracion inicial recomendada. Se podra cambiar mas tarde en Settings.",
  reservationFlowTitle: "Reservation flow settings",
  reservationFlowCopy:
    "En onboarding dejamos el hotel arrancando en modo Confirmed. Es la opcion recomendada por defecto y luego podra cambiarse si el flujo del hotel necesita otra cosa.",
  reservationFlowHint: "Bloqueado durante onboarding. Modo inicial recomendado.",
  requestedTitle: "Requested",
  requestedCopy: "El equipo revisa la reserva antes de pasarla a confirmada.",
  confirmedTitle: "Confirmed",
  confirmedCopy: "La reserva entra directamente como confirmada al guardarse.",
  recommendedTag: "Recomendado",
  lockedTag: "Bloqueado",
  confirmationTitle: "Confirmation Email Studio",
  confirmationCopy:
    "Aqui se muestra el email de confirmacion estandar que quedara configurado al arrancar. El contenido es informativo durante onboarding y podra redisenarse despues.",
  confirmationHint:
    "Durante onboarding solo podras elegir en que idioma o idiomas se enviaran los correos. El contenido del email se mantiene como configuracion inicial y se podra editar despues en Settings.",
  confirmationLanguage: "Idioma",
  confirmationLanguagesLabel: "Idiomas de envio",
  confirmationSubject: "Asunto",
  confirmationBody: "Cuerpo",
  validationConfirmationLanguages:
    "Selecciona al menos un idioma para los correos de confirmacion.",
  externalNotificationTitle: "Hotel Notification Studio",
  externalNotificationCopy:
    "Elige si el hotel debe recibir un email interno cada vez que en Pilcore se cree, modifique o cancele una reserva que llega desde fuera, por ejemplo desde una OTA, una agencia o el channel manager.",
  externalNotificationHint:
    "Durante onboarding solo se guarda la activacion y la lista de destinatarios. Las reservas directas creadas dentro de Pilcore no entran en este aviso, y el contenido del email se podra ajustar despues en Settings.",
  externalNotificationToggleLabel:
    "Activar emails al hotel para cambios en reservas externas",
  externalNotificationToggleCopy:
    "Si esta activo, Pilcore envia un aviso interno por cada evento de reserva externa.",
  externalNotificationRecipientsLabel: "Emails destinatarios",
  externalNotificationRecipientsHint:
    "Pulsa Enter o Anadir para crear chips. Tambien puedes pegar varios destinatarios separados por comas, punto y coma o salto de linea.",
  externalNotificationRecipientPlaceholder: "reservations@hotel.com",
  externalNotificationRecipientEmpty: "Todavia no hay destinatarios configurados.",
  externalNotificationAddAction: "Anadir",
  externalNotificationRemoveAction: "Quitar",
  externalNotificationEnabledTag: "Activo",
  externalNotificationDisabledTag: "Inactivo",
  validationExternalNotificationRecipients:
    "Anade al menos un email destinatario si las notificaciones del hotel estan activadas.",
  summaryTitle: "Resumen rapido",
  summaryIntro:
    "Este paso combina ajustes editables de reserva y alertas internas con tres bloques iniciales bloqueados.",
  summaryPoints: [
    "Guarda cuando las secciones editables reflejen como debe arrancar el hotel.",
    "Required customer roles, reservation flow y confirmation email quedan bloqueados durante onboarding.",
    "Todo lo marcado aqui como recomendado podra ajustarse despues en la parte live de Settings.",
  ],
  summaryCheckinLabel: "Check-in",
  summaryCheckoutLabel: "Check-out",
  summaryAgeLabel: "Edades",
  summaryFieldsLabel: "Datos obligatorios elegidos",
  summaryRolesLabel: "Roles obligatorios",
  summaryFlowLabel: "Modo de flujo",
  summaryEmailLabel: "Idiomas de envio",
  summaryNotificationLabel: "Alertas al hotel",
  summaryEmailValue: "Email inicial estandar",
  summaryNotificationDisabled: "Inactivas",
  summaryNotificationEnabled: (count) =>
    count === 1 ? "Activas - 1 destinatario" : `Activas - ${count} destinatarios`,
  roles: {
    booker: {
      label: "Booker",
      description: "Titular de la reserva, referencia de facturacion y de comunicacion saliente.",
    },
    main_guest: {
      label: "Main guest",
      description: "Huesped principal asociado a la habitacion y a la operativa.",
    },
    accompanying: {
      label: "Acompanante",
      description: "Huesped adulto adicional que se aloja dentro de la reserva.",
    },
    child: {
      label: "Ninos",
      description: "Perfiles de ninos capturados dentro de la reserva.",
    },
  },
  customerFields: [
    { value: "salutation", label: "Tratamiento" },
    { value: "first_name", label: "Nombre" },
    { value: "middle_name", label: "Segundo nombre" },
    { value: "last_name", label: "Apellido" },
    { value: "second_last_name", label: "Segundo apellido" },
    { value: "gender", label: "Genero" },
    { value: "birth_date", label: "Fecha de nacimiento" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Telefono" },
    { value: "country", label: "Pais" },
    { value: "city", label: "Ciudad" },
    { value: "address", label: "Direccion" },
    { value: "postal_code", label: "Codigo postal" },
    { value: "document_type", label: "Tipo de documento" },
    { value: "document_number", label: "Numero de documento" },
    { value: "is_vip", label: "Es VIP" },
    { value: "vip_level", label: "Nivel VIP" },
    { value: "photo_url", label: "URL de foto" },
    { value: "customer_description", label: "Descripcion del cliente" },
  ],
  confirmationLanguages: {
    en: "Ingles",
    es: "Espanol",
  },
};

const CA_MESSAGES = {
  ...BASE_MESSAGES,
  title: "Defineix les bases de la reserva abans de sortir a produccio",
  copy:
    "Configura aqui l'essencial perque l'hotel comenci amb una base operativa clara. Els blocs bloquejats son ajustos inicials recomanats i es podran canviar mes endavant a Settings.",
  loadError: "No hem pogut carregar l'esborrany de reservation settings.",
  saveError: "No hem pogut desar reservation settings ara mateix.",
  saveSuccess: "L'esborrany de reservation settings s'ha desat correctament.",
  validationTimes: "L'hora de check-in i la de check-out son obligatories.",
  validationAgeRange: "L'edat maxima de nen no pot ser inferior a la de bebe.",
  stayTitle: "Stay settings",
  stayCopy: "Tria l'hora estandard de check-in i check-out que es mostrara des de l'inici.",
  checkinTime: "Hora de check-in",
  checkoutTime: "Hora de check-out",
  agesTitle: "Edats dels hostes",
  agesCopy: "Defineix els rangs d'edat inicials que Pilcore fara servir a la configuracio de reserves.",
  maxAgeBaby: "Edat maxima de bebe",
  maxAgeChild: "Edat maxima de nen",
  minAgeMainGuest: "Edat minima del main guest",
  minAgeBooker: "Edat minima del booker",
  requiredFieldsTitle: "Dades obligatories per rol",
  requiredFieldsCopy:
    "Tria quines dades s'han de demanar per a cada rol quan es crea una reserva.",
  requiredFieldsHint:
    "Aixo pot ser limitant per a les reserves directes. No es fara cap reserva directa si aquestes dades no estan informades.\n\nEn reserves de motor de reserves, OTAs o agencies, el criteri sera el marcat per cada OTA, agencia o el mateix motor de reserves, de manera que poden entrar reserves que no compleixin els requeriments de dades obligatories configurats aqui.",
  fieldsSelected: (count) => `${count} seleccionats`,
  requiredRolesTitle: "Required customer roles",
  requiredRolesCopy:
    "Aquesta configuracio inicial queda bloquejada durant l'onboarding. Recomanem comencar nomes amb Booker i ajustar-ho despres si cal.",
  requiredRolesHint: "Configuracio inicial recomanada. Es podra canviar mes tard a Settings.",
  reservationFlowTitle: "Reservation flow settings",
  reservationFlowCopy:
    "Durant l'onboarding deixem l'hotel comencant en mode Confirmed. Es l'opcio recomanada per defecte i despres es podra canviar si el flux de l'hotel necessita una altra cosa.",
  reservationFlowHint: "Bloquejat durant l'onboarding. Mode inicial recomanat.",
  requestedTitle: "Requested",
  requestedCopy: "L'equip revisa la reserva abans de passar-la a confirmada.",
  confirmedTitle: "Confirmed",
  confirmedCopy: "La reserva entra directament com a confirmada quan es desa.",
  recommendedTag: "Recomanat",
  lockedTag: "Bloquejat",
  confirmationTitle: "Confirmation Email Studio",
  confirmationCopy:
    "Aqui es mostra l'email de confirmacio estandard que quedara configurat a l'inici. Es nomes informatiu durant l'onboarding i es podra redissenyar despres.",
  confirmationHint:
    "Durant l'onboarding nomes podras triar en quin idioma o idiomes s'enviaran els correus. El contingut del correu es mante com a configuracio inicial i es podra editar despres a Settings.",
  confirmationLanguage: "Idioma",
  confirmationLanguagesLabel: "Idiomes d'enviament",
  confirmationSubject: "Assumpte",
  confirmationBody: "Cos",
  validationConfirmationLanguages:
    "Selecciona almenys un idioma per als correus de confirmacio.",
  externalNotificationTitle: "Hotel Notification Studio",
  externalNotificationCopy:
    "Tria si l'hotel ha de rebre un correu intern cada vegada que a Pilcore es crea, es modifica o es cancella una reserva que arriba des de fora, per exemple des d'una OTA, una agencia o el channel manager.",
  externalNotificationHint:
    "Durant l'onboarding nomes es desa l'activacio i la llista de destinataris. Les reserves directes creades dins de Pilcore no entren en aquest avis, i el contingut del correu es podra ajustar despres a Settings.",
  externalNotificationToggleLabel:
    "Activar correus a l'hotel per als canvis de reserves externes",
  externalNotificationToggleCopy:
    "Si esta actiu, Pilcore envia un avis intern per cada esdeveniment de reserva externa.",
  externalNotificationRecipientsLabel: "Correus destinataris",
  externalNotificationRecipientsHint:
    "Prem Enter o Afegir per crear xips. Tambe pots enganxar diversos destinataris separats per comes, punt i coma o salts de linia.",
  externalNotificationRecipientPlaceholder: "reservations@hotel.com",
  externalNotificationRecipientEmpty: "Encara no hi ha destinataris configurats.",
  externalNotificationAddAction: "Afegir",
  externalNotificationRemoveAction: "Treure",
  externalNotificationEnabledTag: "Actiu",
  externalNotificationDisabledTag: "Inactiu",
  validationExternalNotificationRecipients:
    "Afegeix almenys un correu destinatari si les notificacions de l'hotel estan activades.",
  summaryTitle: "Resum rapid",
  summaryIntro:
    "Aquest pas combina ajustos editables de reserva i alertes internes amb tres blocs inicials bloquejats.",
  summaryPoints: [
    "Desa quan les seccions editables reflecteixin com ha de comencar l'hotel.",
    "Required customer roles, reservation flow i confirmation email queden bloquejats durant l'onboarding.",
    "Tot allo marcat aqui com a recomanat es podra ajustar despres a la part live de Settings.",
  ],
  summaryCheckinLabel: "Check-in",
  summaryCheckoutLabel: "Check-out",
  summaryAgeLabel: "Edats",
  summaryFieldsLabel: "Dades obligatories triades",
  summaryRolesLabel: "Rols obligatoris",
  summaryFlowLabel: "Mode de flux",
  summaryEmailLabel: "Idiomes d'enviament",
  summaryNotificationLabel: "Alertes a l'hotel",
  summaryEmailValue: "Email inicial estandard",
  summaryNotificationDisabled: "Inactives",
  summaryNotificationEnabled: (count) =>
    count === 1 ? "Actives - 1 destinatari" : `Actives - ${count} destinataris`,
  roles: {
    booker: {
      label: "Booker",
      description: "Titular de la reserva, referencia de facturacio i de comunicacio sortint.",
    },
    main_guest: {
      label: "Main guest",
      description: "Hoste principal associat a l'habitacio i a l'operativa.",
    },
    accompanying: {
      label: "Acompanyant",
      description: "Hoste adult addicional allotjat dins de la reserva.",
    },
    child: {
      label: "Nens",
      description: "Perfils de nens capturats dins de la reserva.",
    },
  },
  customerFields: [
    { value: "salutation", label: "Tractament" },
    { value: "first_name", label: "Nom" },
    { value: "middle_name", label: "Segon nom" },
    { value: "last_name", label: "Cognom" },
    { value: "second_last_name", label: "Segon cognom" },
    { value: "gender", label: "Genere" },
    { value: "birth_date", label: "Data de naixement" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Telefon" },
    { value: "country", label: "Pais" },
    { value: "city", label: "Ciutat" },
    { value: "address", label: "Adreca" },
    { value: "postal_code", label: "Codi postal" },
    { value: "document_type", label: "Tipus de document" },
    { value: "document_number", label: "Numero de document" },
    { value: "is_vip", label: "Es VIP" },
    { value: "vip_level", label: "Nivell VIP" },
    { value: "photo_url", label: "URL de foto" },
    { value: "customer_description", label: "Descripcio del client" },
  ],
  confirmationLanguages: {
    en: "Angles",
    es: "Castella",
  },
};

const RESERVATION_SETTINGS_MESSAGES = {
  en: BASE_MESSAGES,
  es: ES_MESSAGES,
  ca: CA_MESSAGES,
};

export function getReservationSettingsMessages(languageId) {
  return RESERVATION_SETTINGS_MESSAGES[languageId] || RESERVATION_SETTINGS_MESSAGES.en;
}
